import { useState, useEffect, useCallback } from 'react';
import { Plus, MoreHorizontal, Pencil, Trash2, Loader2, ArrowDownAZ } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { PicklistConfig } from '@/config/picklists';
import { basinService, regionService, segmentService, type ServiceContext } from '@/services';
import PicklistValueDialog, { PicklistValue } from './PicklistValueDialog';

type PicklistTableName = 'list_segments' | 'list_basins' | 'list_regions';

interface PicklistValuesTableProps {
  picklist: PicklistConfig;
  companyId: string;
}

// Map picklist table names to their services
const getPicklistService = (tableName: PicklistTableName) => {
  switch (tableName) {
    case 'list_basins':
      return basinService;
    case 'list_regions':
      return regionService;
    case 'list_segments':
      return segmentService;
  }
};

export default function PicklistValuesTable({
  picklist,
  companyId,
}: PicklistValuesTableProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [values, setValues] = useState<PicklistValue[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingValue, setEditingValue] = useState<PicklistValue | null>(null);
  const [editingSnapshot, setEditingSnapshot] = useState<Record<string, unknown> | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [valueToDelete, setValueToDelete] = useState<PicklistValue | null>(null);
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const service = getPicklistService(picklist.tableName as PicklistTableName);

  const getServiceContext = (): ServiceContext => ({
    companyId,
    userId: user?.id,
  });

  const fetchValues = useCallback(async () => {
    setLoading(true);
    try {
      const result = await service.list(companyId);
      if (result.error) throw result.error;
      setValues(result.data || []);
    } catch (error: any) {
      console.error('Error fetching picklist values:', error);
      toast({
        title: 'Error',
        description: 'Failed to load picklist values.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [service, companyId, toast]);

  useEffect(() => {
    fetchValues();
  }, [fetchValues]);

  const handleAddNew = () => {
    setEditingValue(null);
    setEditingSnapshot(null);
    setDialogOpen(true);
  };

  const handleEdit = async (value: PicklistValue) => {
    try {
      // Fetch fresh data with joined profile names for audit display
      const result = await service.getWithAuditNames(value.id);
      if (result.error) throw result.error;
      if (!result.data) throw new Error('Value not found');

      const data = result.data;

      // Capture snapshot for history tracking
      setEditingSnapshot({
        display_value: data.display_value,
        storage_value: data.storage_value,
        sequence: data.sequence,
        is_active: data.is_active,
        is_default: data.is_default,
      });

      setEditingValue(data);
      setDialogOpen(true);
    } catch (error: any) {
      console.error('Error fetching value:', error);
      toast({
        title: 'Error',
        description: 'Failed to load picklist value.',
        variant: 'destructive',
      });
    }
  };

  const handleSave = async (data: Partial<PicklistValue>, keepOpen = false) => {
    setIsSaving(true);
    try {
      const context = getServiceContext();

      if (data.id) {
        // Update using service
        const updateData = {
          display_value: data.display_value,
          storage_value: data.storage_value,
          sequence: data.sequence,
          is_active: data.is_active,
          is_default: data.is_default,
        };

        const result = await service.update(
          data.id,
          updateData,
          editingSnapshot || {},
          context
        );

        if (result.error) throw result.error;
        toast({ title: 'Success', description: 'Picklist value updated.' });
      } else {
        // Insert using service
        const result = await service.create(
          {
            display_value: data.display_value!,
            storage_value: data.storage_value!,
            sequence: data.sequence!,
            is_active: data.is_active ?? true,
            is_default: data.is_default ?? false,
          },
          context
        );

        if (result.error) throw result.error;
        toast({ title: 'Success', description: 'Picklist value created.' });
      }

      await fetchValues();

      if (keepOpen) {
        // Reset to create a new record
        setEditingValue(null);
        setEditingSnapshot(null);
      } else {
        setDialogOpen(false);
      }
    } catch (error: any) {
      console.error('Error saving picklist value:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save picklist value.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (value: PicklistValue) => {
    setValueToDelete(value);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!valueToDelete) return;

    try {
      const snapshot = {
        display_value: valueToDelete.display_value,
        storage_value: valueToDelete.storage_value,
        sequence: valueToDelete.sequence,
        is_active: valueToDelete.is_active,
        is_default: valueToDelete.is_default,
      };

      const result = await service.delete(valueToDelete.id, snapshot, getServiceContext());
      if (result.error) throw result.error;

      setValues((prev) => prev.filter((v) => v.id !== valueToDelete.id));
      toast({ title: 'Success', description: 'Picklist value deleted.' });
    } catch (error: any) {
      console.error('Error deleting value:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete picklist value.',
        variant: 'destructive',
      });
    } finally {
      setDeleteDialogOpen(false);
      setValueToDelete(null);
    }
  };

  const confirmReorderAlphabetically = async () => {
    setIsReordering(true);
    try {
      const context = getServiceContext();
      // Sort values alphabetically by display_value
      const sortedValues = [...values].sort((a, b) =>
        a.display_value.localeCompare(b.display_value)
      );

      // Update each value with its new sequence number using service
      for (let i = 0; i < sortedValues.length; i++) {
        const newSequence = i + 1;
        if (sortedValues[i].sequence !== newSequence) {
          const result = await service.update(
            sortedValues[i].id,
            { sequence: newSequence },
            { sequence: sortedValues[i].sequence },
            context
          );
          if (result.error) throw result.error;
        }
      }

      await fetchValues();
      toast({ title: 'Success', description: 'Picklist values reordered alphabetically.' });
    } catch (error: any) {
      console.error('Error reordering values:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to reorder picklist values.',
        variant: 'destructive',
      });
    } finally {
      setIsReordering(false);
      setReorderDialogOpen(false);
    }
  };

  const nextSequence = values.length > 0 
    ? Math.max(...values.map((v) => v.sequence)) + 1 
    : 1;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">{picklist.name}</h2>
          {picklist.description && (
            <p className="text-sm text-muted-foreground">{picklist.description}</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setReorderDialogOpen(true)} disabled={values.length < 2}>
            <ArrowDownAZ className="w-4 h-4 mr-2" />
            Reorder Alphabetically
          </Button>
          <Button onClick={handleAddNew}>
            <Plus className="w-4 h-4 mr-2" />
            Add Value
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Sequence</th>
              <th>Display Value</th>
              <th>Storage Value</th>
              <th>Status</th>
              <th>Default</th>
              <th className="w-16"></th>
            </tr>
          </thead>
          <tbody>
            {values.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-muted-foreground">
                  No values defined. Click "Add Value" to create one.
                </td>
              </tr>
            ) : (
              values.map((value) => (
                <tr
                  key={value.id}
                  className="cursor-pointer"
                  onClick={() => handleEdit(value)}
                >
                  <td className="font-mono text-sm">{value.sequence}</td>
                  <td className="font-medium">{value.display_value}</td>
                  <td className="font-mono text-sm text-muted-foreground">
                    {value.storage_value}
                  </td>
                  <td>
                    <Badge variant={value.is_active ? 'default' : 'secondary'}>
                      {value.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td>
                    {value.is_default && (
                      <Badge variant="outline">Default</Badge>
                    )}
                  </td>
                  <td>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(value);
                          }}
                        >
                          <Pencil className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(value);
                          }}
                          className="text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PicklistValueDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        value={editingValue}
        onSave={handleSave}
        isLoading={isSaving}
        nextSequence={nextSequence}
        picklist={picklist}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Picklist Value</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{valueToDelete?.display_value}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={reorderDialogOpen} onOpenChange={setReorderDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reorder Alphabetically</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reorder the picklist values alphabetically by Display Value? 
              This will update the sequence numbers for all values.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isReordering}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReorderAlphabetically} disabled={isReordering}>
              {isReordering ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Reordering...
                </>
              ) : (
                'Yes, Reorder'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
